<template>
  <div class="au-formgroup">
    <!-- Token Error State -->
    <template v-if="tokenError">
      <div class="au-card au-card--center au-success">
        <span class="au-env au-env--err">
          <svg
            width="46"
            height="46"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M9 9l6 6M15 9l-6 6" />
          </svg>
        </span>

        <h3 class="au-success-h">{{ STRINGS.auth.passwordReset.tokenErrorHeading }}</h3>

        <button
          type="button"
          class="au-btn au-btn--primary au-mt"
          data-testid="reset-request-new-link"
          @click="emit('navigate', 'forgot-password')"
        >
          {{ STRINGS.auth.links.requestNewLink }}
        </button>
      </div>

      <div class="au-flinks">
        <button
          class="au-link au-link--muted"
          @click="emit('navigate', 'login')"
          data-testid="reset-error-back-to-login"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>

    <!-- Success State -->
    <template v-else-if="resetSuccess">
      <div class="au-card au-card--center au-success">
        <span class="au-env au-env--ok">
          <svg
            width="46"
            height="46"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12.5l2.5 2.5L16 9.5" />
          </svg>
        </span>

        <h3 class="au-success-h">{{ STRINGS.auth.passwordReset.resetSuccessHeading }}</h3>

        <button
          type="button"
          class="au-btn au-btn--primary au-mt"
          data-testid="reset-success-login-button"
          @click="emit('navigate', 'login')"
        >
          {{ STRINGS.auth.buttons.logIn }}
        </button>
      </div>
    </template>

    <!-- Reset Form -->
    <template v-else>
      <form
        class="au-card"
        :aria-label="STRINGS.aria.labels.resetPasswordForm"
        @submit.prevent="handleSubmit"
        autocomplete="on"
        novalidate
      >
        <!-- New Password -->
        <div class="au-field">
          <label for="reset-new-password" class="au-label">
            {{ STRINGS.auth.labels.newPassword }}
          </label>
          <div class="au-input-wrap">
            <InputText
              id="reset-new-password"
              ref="passwordInput"
              v-model="newPassword"
              name="new-password"
              :type="showPassword ? 'text' : 'password'"
              class="au-input au-input--pw"
              :placeholder="STRINGS.auth.placeholders.newPassword"
              autocomplete="new-password"
              :aria-invalid="!!displayError"
              data-testid="reset-new-password-input"
              @keyup.enter="handleSubmit"
            />
            <PasswordToggle :pressed="showPassword" @toggle="showPassword = !showPassword" />
          </div>
          <p class="au-hint">{{ STRINGS.auth.hints.password }}</p>
        </div>

        <!-- Confirm Password -->
        <div class="au-field">
          <label for="reset-confirm-password" class="au-label">
            {{ STRINGS.auth.labels.confirmPassword }}
          </label>
          <div class="au-input-wrap">
            <InputText
              id="reset-confirm-password"
              v-model="confirmPassword"
              name="confirm-password"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="au-input au-input--pw"
              :placeholder="STRINGS.auth.placeholders.confirmPassword"
              autocomplete="new-password"
              :aria-invalid="!!displayError"
              data-testid="reset-confirm-password-input"
              @keyup.enter="handleSubmit"
            />
            <PasswordToggle
              :pressed="showConfirmPassword"
              @toggle="showConfirmPassword = !showConfirmPassword"
            />
          </div>
        </div>

        <!-- Error display -->
        <p
          v-if="displayError"
          ref="errorRef"
          class="au-hint au-hint--err text-center"
          role="alert"
          aria-live="polite"
          tabindex="-1"
        >
          {{ displayError }}
        </p>

        <!-- Submit button -->
        <button
          type="button"
          class="au-btn au-btn--primary au-mt"
          :disabled="!canSubmit"
          data-testid="reset-submit-button"
          @click="handleSubmit"
        >
          <template v-if="passwordReset.isLoading.value">
            {{ STRINGS.auth.buttons.settingPassword }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.auth.buttons.setNewPassword }}
          </template>
        </button>
      </form>

      <!-- Footer links -->
      <div class="au-flinks">
        <button
          class="au-link au-link--muted"
          @click="emit('navigate', 'login')"
          data-testid="reset-back-to-login-link"
        >
          {{ STRINGS.auth.links.backToLogin }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PasswordToggle from './ui/PasswordToggle.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { usePasswordReset } from '@/composables/usePasswordReset';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const props = defineProps<{
  token: string;
}>();

const emit = defineEmits<{
  navigate: [target: 'login' | 'forgot-password' | 'homepage'];
}>();

const passwordReset = usePasswordReset();

const newPassword = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const resetSuccess = ref(false);
const tokenError = ref(false);
const localError = ref('');
const passwordInput = ref();
const errorRef = ref<HTMLElement>();

const displayError = computed(() => {
  if (passwordReset.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(passwordReset.rateLimitCountdown.value);
  }
  if (passwordReset.error.value) {
    return passwordReset.error.value.message;
  }
  return localError.value;
});

const canSubmit = computed(() => {
  return (
    newPassword.value.length > 0 &&
    confirmPassword.value.length > 0 &&
    !passwordReset.isLoading.value &&
    passwordReset.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  localError.value = '';
  passwordReset.clearError();

  if (newPassword.value.length < AUTH_CONFIG.PASSWORD_MIN_LENGTH) {
    localError.value = STRINGS.auth.errors.passwordTooShort;
    focusError();
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    localError.value = STRINGS.auth.errors.passwordsDoNotMatch;
    focusError();
    return;
  }

  try {
    await passwordReset.confirmReset(props.token, newPassword.value);
    resetSuccess.value = true;
  } catch (err: unknown) {
    if (isAuthError(err) && (err.type === 'invalid_token' || err.type === 'expired_token')) {
      tokenError.value = true;
    } else if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthResetPasswordUnexpected);
    }
    focusError();
  }
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = passwordInput.value?.$el ?? passwordInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>
